import { toastr } from 'react-redux-toastr';

import { downloadFile } from 'actions/common/downloadFile';
import { handleException } from 'actions/exceptions';
import WhdStatementExportStatus from 'common/Forms/WhdStatementExportStatus';
import { ApiService } from 'services';

export const GET_WHD_STMT_ZIP_EXPORT_STATUS_REQUEST = 'GET_WHD_STMT_ZIP_EXPORT_STATUS_REQUEST';
export const GET_WHD_STMT_ZIP_EXPORT_STATUS_SUCCESS = 'GET_WHD_STMT_ZIP_EXPORT_STATUS_SUCCESS';
export const GET_WHD_STMT_ZIP_EXPORT_STATUS_FAILURE = 'GET_WHD_STMT_ZIP_EXPORT_STATUS_FAILURE';
export const GENERATE_WHD_STMT_ZIP_EXPORT_REQUEST = 'GENERATE_WHD_STMT_ZIP_EXPORT_REQUEST';
export const GENERATE_WHD_STMT_ZIP_EXPORT_SUCCESS = 'GENERATE_WHD_STMT_ZIP_EXPORT_SUCCESS';
export const GENERATE_WHD_STMT_ZIP_EXPORT_FAILURE = 'GENERATE_WHD_STMT_ZIP_EXPORT_FAILURE';
export const DOWNLOAD_WHD_STMT_ZIP_EXPORT_REQUEST = 'DOWNLOAD_WHD_STMT_ZIP_EXPORT_REQUEST';
export const DOWNLOAD_WHD_STMT_ZIP_EXPORT_SUCCESS = 'DOWNLOAD_WHD_STMT_ZIP_EXPORT_SUCCESS';
export const DOWNLOAD_WHD_STMT_ZIP_EXPORT_FAILURE = 'DOWNLOAD_WHD_STMT_ZIP_EXPORT_FAILURE';
export const CANCEL_WHD_STMT_ZIP_EXPORT_REQUEST = 'CANCEL_WHD_STMT_ZIP_EXPORT_REQUEST';
export const CANCEL_WHD_STMT_ZIP_EXPORT_SUCCESS = 'CANCEL_WHD_STMT_ZIP_EXPORT_SUCCESS';
export const CANCEL_WHD_STMT_ZIP_EXPORT_FAILURE = 'CANCEL_WHD_STMT_ZIP_EXPORT_FAILURE';

function requestGetWhdStatementZipExportStatus() {
  return {
    type: GET_WHD_STMT_ZIP_EXPORT_STATUS_REQUEST,
    isGettingZipExportStatus: true
  };
}

function receiveGetWhdStatementZipExportStatus(zipExportResponse) {
  return {
    type: GET_WHD_STMT_ZIP_EXPORT_STATUS_SUCCESS,
    isGettingZipExportStatus: false,
    zipExportResponse
  };
}

function rejectGetWhdStatementZipExportStatus() {
  return {
    type: GET_WHD_STMT_ZIP_EXPORT_STATUS_FAILURE,
    isGettingZipExportStatus: false
  };
}

function requestGenerateWhdStatementZipExport() {
  return {
    type: GENERATE_WHD_STMT_ZIP_EXPORT_REQUEST,
    isGeneratingZipExport: true
  };
}

function receiveGenerateWhdStatementZipExport() {
  return {
    type: GENERATE_WHD_STMT_ZIP_EXPORT_SUCCESS,
    isGeneratingZipExport: false,
    zipExportStatus: WhdStatementExportStatus.Processing
  };
}

function rejectGenerateWhdStatementZipExport() {
  return {
    type: GENERATE_WHD_STMT_ZIP_EXPORT_FAILURE,
    isGeneratingZipExport: false
  };
}

function requestCancelWhdStatementZipExport() {
  return {
    type: CANCEL_WHD_STMT_ZIP_EXPORT_REQUEST,
    isGeneratingZipExport: true
  };
}

function receiveCancelWhdStatementZipExport() {
  return {
    type: CANCEL_WHD_STMT_ZIP_EXPORT_SUCCESS,
    isGeneratingZipExport: false,
    zipExportStatus: WhdStatementExportStatus.NotStarted
  };
}

function rejectCancelWhdStatementZipExport() {
  return {
    type: CANCEL_WHD_STMT_ZIP_EXPORT_FAILURE,
    isGeneratingZipExport: false
  };
}

function requestDownloadWhdStatementZipExport() {
  return {
    type: DOWNLOAD_WHD_STMT_ZIP_EXPORT_REQUEST,
    isDownloadingZipExport: true
  };
}

function receiveDownloadWhdStatementZipExport() {
  return {
    type: DOWNLOAD_WHD_STMT_ZIP_EXPORT_SUCCESS,
    isDownloadingZipExport: false
  };
}

function rejectDownloadWhdStatementZipExport() {
  return {
    type: DOWNLOAD_WHD_STMT_ZIP_EXPORT_FAILURE,
    isDownloadingZipExport: false
  };
}

function parseZipExportStatus(status) {
  const parsedStatus = typeof status === 'string' ? parseInt(status, 10) : status;

  if (parsedStatus === null || typeof parsedStatus === 'undefined' || Number.isNaN(parsedStatus)) {
    return WhdStatementExportStatus.NotStarted;
  }

  return parsedStatus;
}

function parseFileName(response, accountId) {
  let fileName = `withholding-statement-${ accountId }.zip`;

  if (response.headers['content-disposition']) {
    const [, dispositionFileName] = response.headers['content-disposition'].split('filename=');
    const [parsedFileName] = dispositionFileName.split(';');
    fileName = parsedFileName;
    const firstChar = fileName.substring(0, 1);
    if (firstChar === '"') {
      fileName = fileName.slice(1, -1);
    }
  }

  return fileName;
}

export function getWhdStatementZipExportStatus(accountId, versionNumber) {
  return (dispatch) => {
    dispatch(requestGetWhdStatementZipExportStatus());

    const promise = () => ApiService
      .getWhdStatementZipExportStatus(accountId, versionNumber)
      .then((response) => {
        const data = response?.data;
        const status = parseZipExportStatus(typeof data === 'object' ? data?.status : data);
        const failedFiles = typeof data === 'object' ? data?.failedFiles : null;
        dispatch(receiveGetWhdStatementZipExportStatus({ status, failedFiles }));
      }).catch((error) => {
        if (!error.response && error.retries && error.retries > 1) {
          ApiService.addQueuedPromise(promise, error);
        }
        if (error.response || (error.retries && error.retries > 1)) {
          dispatch(handleException(error, rejectGetWhdStatementZipExportStatus, false, false));
        }
      });

    return promise();
  };
}

export function generateWhdStatementZipExport(accountId, versionNumber) {
  return (dispatch) => {
    dispatch(requestGenerateWhdStatementZipExport());

    const promise = () => ApiService
      .generateWhdStatementZipExport(accountId, versionNumber)
      .then(() => {
        dispatch(receiveGenerateWhdStatementZipExport());
        toastr.success('', 'Statement Zip Export Generation has started');
        return dispatch(getWhdStatementZipExportStatus(accountId, versionNumber));
      }).catch((error) => {
        if (!error.response && error.retries && error.retries > 1) {
          ApiService.addQueuedPromise(promise, error);
        }
        if (error.response || (error.retries && error.retries > 1)) {
          toastr.error('', 'Generation has failed');
          dispatch(handleException(error, rejectGenerateWhdStatementZipExport, false, false));
        }
      });

    return promise();
  };
}

export function cancelWhdStatementZipExport(accountId, versionNumber) {
  return (dispatch) => {
    dispatch(requestCancelWhdStatementZipExport());

    const promise = () => ApiService
      .cancelWhdStatementZipExport(accountId, versionNumber)
      .then(() => {
        dispatch(receiveCancelWhdStatementZipExport());
        toastr.success('', 'Statement Zip Export has been cancelled.');
        return dispatch(getWhdStatementZipExportStatus(accountId, versionNumber));
      }).catch((error) => {
        if (!error.response && error.retries && error.retries > 1) {
          ApiService.addQueuedPromise(promise, error);
        }
        if (error.response || (error.retries && error.retries > 1)) {
          toastr.error('', 'Cancellation has failed');
          dispatch(handleException(error, rejectCancelWhdStatementZipExport, false, false));
        }
      });

    return promise();
  };
}

export function downloadWhdStatementZipExport(accountId, versionNumber) {
  return (dispatch) => {
    dispatch(requestDownloadWhdStatementZipExport());

    const promise = () => ApiService
      .downloadWhdStatementZipExport(accountId, versionNumber, { responseType: 'arraybuffer' })
      .then((response) => {
        dispatch(receiveDownloadWhdStatementZipExport());
        return dispatch(downloadFile(response.data, parseFileName(response, accountId), 'application/zip'));
      }).catch((error) => {
        if (!error.response && error.retries && error.retries > 1) {
          ApiService.addQueuedPromise(promise, error);
        }
        if (error.response || (error.retries && error.retries > 1)) {
          dispatch(handleException(error, rejectDownloadWhdStatementZipExport, true));
        }
      });

    return promise();
  };
}
